import CtaArrow from "@/components/svg/CtaArrow";
import ShareButton from "@/components/ShareButton";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

import { getTitleComponents, getTextComponents } from "@/helpers";
import TagIcon from "@/components/TagIcon";


/**
 * @param {import("@prismicio/client").Repeatable<import("@prismicio/client").LinkField>} ctas
 */
const HeroCtas = ({ ctas }) => {
  if (!Array.isArray(ctas) || ctas.length === 0) {
    return null;
  }

  const filledCtas = ctas.filter((cta) => isFilled.link(cta));

  if (filledCtas.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:gap-5">
      {filledCtas.map((cta, index) => {
        const isPrimary = cta.variant === "Primary";

        return (
          <PrismicNextLink prefetch={false}
            key={cta.key ?? index}
            field={cta}
            className={`flex flex-row gap-5 cta cta-text ${isPrimary ?
              "bg-rouge text-blanc" :
              "text-blanc underline"}`}
          >
            {cta.text}
            {isPrimary ? <CtaArrow className={"text-blanc"} /> : null}
          </PrismicNextLink>
        );
      })}
    </div>
  );
};


const HeroContent = ({ title, texte, ctas }) => (
  <div className="flex flex-col gap-8 px-5 py-10 md:py-20 lg:py-0 md:px-10 relative z-10">
    {isFilled.richText(title) && (
      <PrismicRichText field={title} components={getTitleComponents({ color: "blanc" })} />
    )}
    {isFilled.richText(texte) && (
      <PrismicRichText field={texte} components={getTextComponents({ variation: "lg", color: "blanc" })} />
    )}
    <HeroCtas ctas={ctas} />
  </div>
);

/**
 * @param {import("@prismicio/client").ImageField} picture
 * @param {import("@prismicio/client").ImageField} mobilePicture
 * @param {string} [className]
 */
const HeroPicture = ({ picture, mobilePicture, className = "" }) => {
  const desktopImage = isFilled.image(picture) ? picture : mobilePicture;
  const mobileImage = isFilled.image(mobilePicture) ? mobilePicture : picture;

  if (!isFilled.image(desktopImage) && !isFilled.image(mobileImage)) {
    return null;
  }

  return (
    <div className="relative flex flex-col gap-8 overflow-hidden h-full">
      {isFilled.image(desktopImage) && (
        <PrismicNextImage
          field={desktopImage}
          loading="eager"
          fetchPriority="high"
          className={`object-cover hidden h-full w-full absolute absolute-center lg:block ${className}`}
          sizes="100vw"
        />
      )}
      {isFilled.image(mobileImage) && (
        <PrismicNextImage
          field={mobileImage}
          loading="eager"
          fetchPriority="high"
          className={`object-cover h-[400px] md:h-[500px] block lg:hidden ${className} w-full`}
          sizes="100vw"
        />
      )}
    </div>
  );
};

const BackgroundPattern = () => (
  <div
    aria-hidden
    className="absolute inset-x-0 top-0 h-full opacity-15 z-0"
    style={{
      backgroundImage: "url('/icons/hero-pattern.svg')",
      backgroundRepeat: "repeat",
      backgroundSize: "60px 96px",
    }}
  />
);

/**
 * @typedef {import("@prismicio/client").Content.HeroSliceDefault} DefaultHeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<DefaultHeroSlice>} DefaultHeroProps
 * @type {import("react").FC<DefaultHeroProps>}
 */
const DefaultHero = ({ slice }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="contre-uno bg-bleu padding-x padding-y-first overflow-hidden"
  >
    <BackgroundPattern />

    <div className="framing-lg flex flex-col xl:flex-row xl:items-end gap-10 xl:gap-20 relative z-10 md:py-10">
      <div className="md:min-w-160">
        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={getTitleComponents({ color: "blanc", variation: "xl" })}
          />
        )}
      </div>

      <div className="flex flex-col gap-14">
        {isFilled.richText(slice.primary.texte) && (
          <PrismicRichText
            field={slice.primary.texte}
            components={{
              paragraph: ({ children }) => (
                <p className="p-lg-medium md:p-xl-semibold text-blanc">{children}</p>
              ),
            }}
          />
        )}
        <HeroCtas ctas={slice.primary.ctas} />
      </div>
    </div>
  </section>
);

/**
 * @typedef {import("@prismicio/client").Content.HeroSliceHeroPicture} HeroPictureSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HeroPictureSlice>} HeroPictureProps
 * @type {import("react").FC<HeroPictureProps>}
 */
const HeroWithPicture = ({ slice }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="contre-uno bg-bleu"
  >
    <BackgroundPattern />
    <div className="flex flex-col lg:flex-row items-stretch justify-center">
      <div className="w-full lg:w-[45%] h-auto">
        <HeroPicture
          picture={slice.primary.picture}
          mobilePicture={slice.primary.mobile_picture}
        />
      </div>
      <div className="w-full lg:w-[55%] lg:pt-30 lg:pb-25">
        <HeroContent
          title={slice.primary.title}
          texte={slice.primary.texte}
          ctas={slice.primary.ctas}
        />
      </div>
    </div>
  </section>
);

/**
 * @typedef {import("@prismicio/client").Content.HeroSliceImage} ImageHeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ImageHeroSlice>} ImageHeroProps
 * @type {import("react").FC<ImageHeroProps>}
 */
const ImageHero = ({ slice }) => {
  const { picture, mobile_picture } = slice.primary;
  const desktopImage = isFilled.image(picture) ? picture : mobile_picture;
  const mobileImage = isFilled.image(mobile_picture) ? mobile_picture : picture;

  if (!isFilled.image(desktopImage) && !isFilled.image(mobileImage)) {
    return null;
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno w-full overflow-hidden"
    >
      <div className="relative aspect-[39/40] w-full md:aspect-[1440/635]">
        {isFilled.image(desktopImage) && (
          <PrismicNextImage
            field={desktopImage}
            fill
            loading="eager"
            fetchPriority="high"
            className="hidden object-cover md:block"
            sizes="100vw"
          />
        )}
        {isFilled.image(mobileImage) && (
          <PrismicNextImage
            field={mobileImage}
            fill
            loading="eager"
            fetchPriority="high"
            className="object-cover md:hidden"
            sizes="100vw"
          />
        )}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-2 z-0 h-12 -left-[15px]"
          style={{
            backgroundImage: "url('/icons/hero-pattern-light.svg')",
            backgroundRepeat: "repeat-x",
            backgroundSize: "48px 60px",
          }}
        />
      </div>
    </section>
  );
};

const LightHero = ({ slice }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="sticky top-0 z-0 overflow-hidden bg-bleu-50 padding-x padding-y-first"
  >
    <div
      aria-hidden
      className="absolute inset-x-0 left-0 top-0 z-0 h-full opacity-10"
      style={{
        backgroundImage: "url('/icons/aztk-tilable-logo.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "50px",
      }}
    />
    <div className="framing flex flex-col justify-center items-center gap-[20px] lg:gap-[30px] py-15 relative">
      <PrismicRichText
        field={slice.primary.title}
        components={{
          heading1: ({ children }) => (
            <h1 className="h1-n lg:dk text-blanc text-center">{children}</h1>
          ),
        }}
      />
      <PrismicRichText
        field={slice.primary.text}
        components={{
          paragraph: ({ children }) => (
            <p className="p-lg text-blanc text-center">{children}</p>
          ),
        }}
      />
    </div>
  </section>
);

/**
 * @param {import("@prismicio/client").Repeatable<import("@prismicio/client").LinkField>} ctas
 */
const TexteHeroCtas = ({ ctas }) => {
  const filledCtas = Array.isArray(ctas) ? ctas.filter((cta) => isFilled.link(cta)) : [];

  if (filledCtas.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-5 md:flex-row md:items-start md:gap-[30px]">
      {filledCtas.map((cta, index) =>
        cta.variant === "Primary" ? (
          <PrismicNextLink
            prefetch={false}
            key={cta.key ?? index}
            field={cta}
            className="cta cta-text w-fit bg-rouge text-beige-30"
          >
            {cta.text}
            <CtaArrow className="text-beige-30" />
          </PrismicNextLink>
        ) : (
          <PrismicNextLink
            prefetch={false}
            key={cta.key ?? index}
            field={cta}
            className="cta cta-text w-fit text-bleu-90 underline underline-offset-2"
          >
            {cta.text}
          </PrismicNextLink>
        ),
      )}
    </div>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.HeroSliceTexte} TexteHeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<TexteHeroSlice>} TexteHeroProps
 * @type {import("react").FC<TexteHeroProps>}
 */
const TexteHero = ({ slice }) => {
  const { sub_header, title, text, ctas } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno relative overflow-hidden bg-beige padding-x pb-[100px] pt-[110px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 left-0 top-0 z-0 h-full opacity-15"
        style={{
          backgroundImage: "url('/icons/aztk-tilable-logo-white.svg')",
          backgroundRepeat: "repeat",
          backgroundSize: "50px",
        }}
      />

      <div className="framing-lg relative lg:py-[60px]">
        <div className="flex max-w-[1000px] flex-col gap-[30px]">
          <div className="flex flex-col gap-5 lg:gap-[30px]">
            {(isFilled.keyText(sub_header) || isFilled.richText(title)) && (
              <div className="flex flex-col gap-5 lg:gap-2">
                {isFilled.keyText(sub_header) && (
                  <p className="section-text-n text-rouge">{sub_header}</p>
                )}
                {isFilled.richText(title) && (
                  <PrismicRichText
                    field={title}
                    components={{
                      heading1: ({ children }) => (
                        <h1 className="h1-n lg:dk text-bleu">{children}</h1>
                      ),
                      heading2: ({ children }) => (
                        <h1 className="h1-n lg:dk text-bleu">{children}</h1>
                      ),
                    }}
                  />
                )}
              </div>
            )}
          {isFilled.richText(text) && (
            <PrismicRichText
              field={text}
              components={{
                paragraph: ({ children }) => (
                  <p className="p-lg-n text-bleu">{children}</p>
                ),
                strong: ({ children }) => <strong className="md">{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                hyperlink: ({ children, node }) => (
                  <a href={node.data.url} className="underline underline-offset-2">
                    {children}
                  </a>
                ),
              }}
            />
          )}
          </div>
          <TexteHeroCtas ctas={ctas} />
        </div>
      </div>
    </section>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.HeroSliceTitle} TitleHeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<TitleHeroSlice>} TitleHeroProps
 * @type {import("react").FC<TitleHeroProps>}
 */
const TitleHero = ({ slice }) => {
  const { sub_header, title } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno relative overflow-hidden bg-beige padding-x pb-[100px] pt-[110px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 left-0 top-0 z-0 h-full opacity-15"
        style={{
          backgroundImage: "url('/icons/aztk-tilable-logo-white.svg')",
          backgroundRepeat: "repeat",
          backgroundSize: "50px",
        }}
      />

      <div className="framing-lg relative flex flex-col items-start gap-5 lg:items-center lg:gap-2 lg:py-[60px]">
        {isFilled.keyText(sub_header) && (
          <p className="section-text-n text-rouge lg:text-center">{sub_header}</p>
        )}
        {isFilled.richText(title) && (
          <div className="w-full lg:max-w-[1000px]">
            <PrismicRichText
              field={title}
              components={{
                heading1: ({ children }) => (
                  <h1 className="h1-n lg:dk text-bleu lg:text-center">{children}</h1>
                ),
                heading2: ({ children }) => (
                  <h1 className="h1-n lg:dk text-bleu lg:text-center">{children}</h1>
                ),
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

const HeroGuide = ({ slice }) => (
  <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    className="sticky top-0 z-0 overflow-hidden bg-bleu-50 padding-x padding-y-first"
  >
    <div
      aria-hidden
      className="absolute inset-x-0 left-0 top-0 z-0 h-full opacity-10"
      style={{
        backgroundImage: "url('/icons/aztk-tilable-logo.svg')",
        backgroundRepeat: "repeat",
        backgroundSize: "50px",
      }}
    />
    <div className="framing-sm flex flex-col justify-center items-center gap-[20px] lg:gap-[30px] py-15 relative">
      <PrismicRichText
        field={slice.primary.title}
        components={{
          heading1: ({ children }) => (
            <h1 className="h1-n lg:h1-ndk text-blanc text-center">{children}</h1>
          ),
        }}
      />
      <TagIcon tag={slice.primary.type} isGuide={true} color="blanc" extra={[`${slice.primary.articles_amount} articles`]} />
    </div>
  </section>
);

/**
 * @typedef {import("@prismicio/client").Content.HeroSlice} HeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HeroSlice>} HeroProps
 * @type {import("react").FC<HeroProps>}
 */
const Hero = ({ slice }) => {
  switch (slice.variation) {
    case "heroPicture":
      return <HeroWithPicture slice={slice} />;
    case "image":
      return <ImageHero slice={slice} />;
    case "light":
      return <LightHero slice={slice} />;
    case "heroGuide":
      return <HeroGuide slice={slice} />;
    case "texte":
      return <TexteHero slice={slice} />;
    case "title":
      return <TitleHero slice={slice} />;
    default:
      return <DefaultHero slice={slice} />;
  }
};

export default Hero;
